"use client";

import { useState } from "react";
import Link from "next/link";
import { Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { UnderlineInput } from "@/components/UnderlineInput";
import { OTPInput } from "@/components/OTPInput";
import { PARTICIPANT_OTP_LENGTH } from "@/lib/participant-otp-constants";

export default function LoginPage() {
  const [step, setStep] = useState<"email" | "otp">("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/auth/request-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Failed to send OTP");
      setStep("otp");
      setResendCooldown(60);
      const interval = setInterval(() => {
        setResendCooldown((c) => {
          const next = c <= 0 ? 0 : c - 1;
          if (next <= 0) clearInterval(interval);
          return next;
        });
      }, 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/auth/verify-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, code: otp }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Invalid OTP");
      window.location.href = "/home";
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/auth/request-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Failed to resend");
      setResendCooldown(60);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to resend");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex w-full flex-col items-center gap-10 px-4">
      <div className="flex w-full flex-col items-center gap-3">
        <Logo variant="icon" size={220} className="drop-shadow-[0_0_30px_rgba(221,30,37,0.25)]" />
        <p className="font-heading text-center text-xs uppercase tracking-[0.3em] text-white/50">
          Institute for Real Estate and Infrastructure Strategy
        </p>
      </div>

      {step === "email" ? (
        <form onSubmit={handleEmailSubmit} className="flex w-full max-w-md flex-col gap-6">
          <div>
            <label className="mb-2 block text-xs font-medium uppercase tracking-wider text-[var(--iris-text-muted)]">
              Email address
            </label>
            <UnderlineInput
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              icon={<Mail className="h-4 w-4" />}
              required
            />
          </div>
          {error && <p className="text-sm text-[var(--iris-red)]">{error}</p>}
          <Button
            type="submit"
            disabled={loading}
            className="h-12 text-base font-semibold tracking-wide"
          >
            {loading ? "Sending…" : "LOG IN"}
          </Button>
          <div className="flex items-center justify-between text-sm">
            <Link
              href="/admin/login"
              className="text-[var(--iris-text-muted)] transition-colors hover:text-white"
            >
              Organisation login
            </Link>
            <Link
              href="#"
              className="text-[var(--iris-text-muted)] transition-colors hover:text-white"
            >
              Forgot password?
            </Link>
          </div>

          <div className="mt-2 rounded-lg border border-[var(--iris-surface3)] bg-[var(--iris-surface1)] p-4 text-center">
            <p className="text-sm text-[var(--iris-text-muted)]">
              Have a session code?{" "}
              <Link
                href="/join"
                className="font-semibold text-[var(--iris-red)] transition-colors hover:text-white"
              >
                Join Live Session
              </Link>
            </p>
          </div>
        </form>
      ) : (
        <form onSubmit={handleOtpSubmit} className="flex w-full max-w-md flex-col gap-6">
          <p className="text-center text-sm text-[var(--iris-text-muted)]">
            Enter the {PARTICIPANT_OTP_LENGTH}-digit code sent to{" "}
            <span className="text-white">{email}</span>
          </p>
          <OTPInput value={otp} onChange={setOtp} error={!!error} disabled={loading} />
          {error && <p className="text-center text-sm text-[var(--iris-red)]">{error}</p>}
          <Button
            type="submit"
            disabled={loading || otp.length !== PARTICIPANT_OTP_LENGTH}
            className="h-12 text-base font-semibold tracking-wide"
          >
            {loading ? "Verifying…" : "VERIFY"}
          </Button>
          <div className="text-center text-sm text-[var(--iris-text-muted)]">
            {resendCooldown > 0 ? (
              <span>Resend in 0:{String(resendCooldown).padStart(2, "0")}</span>
            ) : (
              <button
                type="button"
                onClick={handleResend}
                className="text-[var(--iris-red)] hover:underline"
              >
                Resend OTP
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  );
}
